import { AbstractDialogData } from '@next/core-lib/dialog';

import { DiseaseContext } from '../../components/disease-overview-table.component/models/models';
import { SprayTypes } from '../../models';

export interface SprayApplicationCropField {
  cropFieldId: number;
  cropFieldName: string;
  areaInHectares: number;
}

export interface SprayApplicationDialogData extends AbstractDialogData {
  diseaseContext: DiseaseContext;
  cropFields: SprayApplicationCropField[];
  defaultDate?: Date;
  initialSprayType: SprayTypes;
  readOnly?: boolean;
}

export interface SprayApplicationDialogResult {
  modifiedCropFieldIds: number[];
}

export interface SprayCocktailApiResponse {
  sprayCocktails: SprayCocktail[];
}

export interface SprayCocktail {
  id: number;
  preferred: boolean;
  sprayCocktailDescription: string;
  cropAids: SprayApplicationCropAid[];
}

export interface SprayApplicationCropAid {
  cropAidId?: number | null;
  name: string;
  dosage: number;
  dosageUnit: string;
  cropAidCtgbNumber: string;
}

export interface SprayApplicationHistoryApiResponse {
  cropFields: SprayApplicationHistoryCropField[];
}

export interface SprayApplicationHistoryCropField {
  id: number;
  name: string;
  sprayApplications: SprayApplicationHistoryEntry[];
}

export interface SprayApplicationHistoryEntry {
  id: string;
  applicationDate: Date;
  sprayCocktailDescription?: string | null;
  cropAids: SprayApplicationCropAid[];
}

export interface CreateSprayApplicationRequest {
  cropFieldIds: number[];
  applicationDate: Date;
  sprayId: number;
  disease: BackendDiseaseType;
}

export interface SprayCocktailOption {
  id: number;
  preferred: boolean;
  name: string;
  cropAids: SprayApplicationCropAid[];
}

export interface SprayApplicationHistoryRow {
  id: string;
  applicationDate: Date;
  sprayNumber: number;
  name: string;
  cropAids: SprayApplicationCropAid[];
}

export interface SprayApplicationHistoryGroup {
  cropFieldId: number;
  cropFieldName: string;
  sprays: SprayApplicationHistoryRow[];
}

export type BackendDiseaseType = 'Phytophthora' | 'DownyMildew' | 'Virus';
export type BackendSprayType = 'Preventive' | 'Curative' | 'Stop';
export interface ParsedSprayApplicationTime {
  hours: number;
  minutes: number;
}

const TWENTY_FOUR_HOUR_TIME_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;
const COMPACT_TWENTY_FOUR_HOUR_TIME_PATTERN = /^(\d{3}|\d{4})$/;

export const mapDiseaseContextToBackendDisease = (diseaseContext: DiseaseContext): BackendDiseaseType => {
  switch (diseaseContext) {
    case 'blight':
      return 'Phytophthora';
    case 'downy-mildew':
      return 'DownyMildew';
    case 'virus-monitor':
      return 'Virus';
  }
};

export const mapSprayTypeToBackendSprayType = (sprayType: SprayTypes): BackendSprayType | undefined => {
  switch (sprayType) {
    case SprayTypes.PreventiveSpray:
      return 'Preventive';
    case SprayTypes.CurativeSpray:
      return 'Curative';
    case SprayTypes.StopSpray:
      return 'Stop';
    default:
      return undefined;
  }
};

export const parseSprayApplicationTime = (time: string): ParsedSprayApplicationTime | null => {
  const trimmedTime = time.trim();
  if (!trimmedTime) {
    return null;
  }

  if (TWENTY_FOUR_HOUR_TIME_PATTERN.test(trimmedTime)) {
    const [hoursText = '0', minutesText = '0'] = trimmedTime.split(':');
    return {
      hours: Number(hoursText),
      minutes: Number(minutesText),
    };
  }

  if (!COMPACT_TWENTY_FOUR_HOUR_TIME_PATTERN.test(trimmedTime)) {
    return null;
  }

  const hours = Number(trimmedTime.slice(0, -2));
  const minutes = Number(trimmedTime.slice(-2));

  if (hours > 23 || minutes > 59) {
    return null;
  }

  return { hours, minutes };
};

export const formatSprayApplicationTime = ({ hours, minutes }: ParsedSprayApplicationTime): string =>
  `${`${hours}`.padStart(2, '0')}:${`${minutes}`.padStart(2, '0')}`;

export const normalizeSprayApplicationTime = (time: string): string => {
  const parsedTime = parseSprayApplicationTime(time);
  return parsedTime ? formatSprayApplicationTime(parsedTime) : time.replace(/[^\d:]/g, '').slice(0, 5);
};

export const sanitizeSprayApplicationTimeInput = (time: string): string => {
  const digitsAndColonsOnly = time.replace(/[^\d:]/g, '');
  if (!digitsAndColonsOnly.includes(':')) {
    return digitsAndColonsOnly.slice(0, 4);
  }

  const [rawHours = '', rawMinutes = ''] = digitsAndColonsOnly.split(':');
  const hours = rawHours.slice(0, 2);
  const minutes = rawMinutes.slice(0, 2);

  return `${hours}:${minutes}`;
};

export const buildSprayApplicationDate = (date: Date, time: string): Date => {
  // Coerce to native Date to support MomentDateAdapter returning a Moment object from the datepicker
  const nativeDate = new Date(+date);
  const parsedTime = parseSprayApplicationTime(time);
  const hours = parsedTime?.hours ?? 0;
  const minutes = parsedTime?.minutes ?? 0;

  return new Date(Date.UTC(nativeDate.getFullYear(), nativeDate.getMonth(), nativeDate.getDate(), hours, minutes));
};

export const formatDefaultTime = (date: Date): string =>
  formatSprayApplicationTime({ hours: date.getHours(), minutes: date.getMinutes() });

export const formatDosage = (dosage: number, locale: string): string =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(dosage);

export const buildSprayName = (cropAids: SprayApplicationCropAid[], locale: string): string =>
  cropAids
    .map((cropAid) => `${cropAid.name} (${formatDosage(cropAid.dosage, locale)} ${cropAid.dosageUnit})`)
    .join(' + ');

export const buildSprayCocktailOptions = (sprayCocktails: SprayCocktail[], locale: string): SprayCocktailOption[] =>
  sprayCocktails.map((sprayCocktail) => ({
    id: sprayCocktail.id,
    preferred: sprayCocktail.preferred,
    name: sprayCocktail.sprayCocktailDescription || buildSprayName(sprayCocktail.cropAids, locale),
    cropAids: sprayCocktail.cropAids,
  }));

export const buildSprayApplicationHistoryGroups = (
  cropFields: SprayApplicationHistoryCropField[],
  locale: string,
): SprayApplicationHistoryGroup[] =>
  cropFields.map((cropField) => {
    const chronologicalSprays = [...cropField.sprayApplications].sort(
      (left, right) =>
        left.applicationDate.getTime() - right.applicationDate.getTime() || left.id.localeCompare(right.id),
    );

    const sprayNumbers = new Map(chronologicalSprays.map((spray, index) => [spray.id, index + 1]));
    const newestFirst = [...cropField.sprayApplications].sort(
      (left, right) =>
        right.applicationDate.getTime() - left.applicationDate.getTime() || right.id.localeCompare(left.id),
    );

    return {
      cropFieldId: cropField.id,
      cropFieldName: cropField.name,
      sprays: newestFirst.map((spray) => ({
        id: spray.id,
        applicationDate: spray.applicationDate,
        sprayNumber: sprayNumbers.get(spray.id) ?? 0,
        name: spray.sprayCocktailDescription || buildSprayName(spray.cropAids, locale),
        cropAids: spray.cropAids,
      })),
    };
  });
